import React, { useState, useEffect } from 'react';
import { CountryLabel } from '../../../shared/components/CountryFlag';
import {
    Card,
    Row,
    Col,
    Statistic,
    Select,
    Space,
    Typography,
    Table,
    Empty,
    Spin,
    message,
    Divider,
    DatePicker,
    Tabs,
    Alert
} from 'antd';
import {
    LineChartOutlined,
    ThunderboltOutlined,
    CloudServerOutlined,
    GlobalOutlined,
    UserOutlined
} from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import dayjs from 'dayjs';
import WafAnalytics from './WafAnalytics';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Analytics = () => {
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(7, 'day'),
        dayjs()
    ]);

    // Admin filters
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [users, setUsers] = useState([]);
    const [domains, setDomains] = useState([]);
    const [userSearch, setUserSearch] = useState('');
    const [domainSearch, setDomainSearch] = useState('');
    const [panelErrors, setPanelErrors] = useState({});
    const [tab, setTab] = useState('traffic');

    const [stats, setStats] = useState({
        traffic: [],
        status: [],
        topUrls: [],
        browsers: [],
        totalRequests: 0,
        totalTraffic: 0,
        cacheHitRate: 0,
        avgResponseTime: 0
    });

    useEffect(() => {
        loadUsers();
    }, [userSearch]);

    useEffect(() => {
        loadDomains();
    }, [selectedUser, domainSearch]);

    useEffect(() => {
        if (dateRange && dateRange[0] && dateRange[1]) {
            loadAllStats();
        }
    }, [dateRange, selectedDomain, selectedUser]);

    const loadUsers = async () => {
        try {
            const response = await apiClient.get('/api/admin/users', {
                params: { search: userSearch }
            });
            if (response.data.success) {
                setUsers(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };

    const loadDomains = async () => {
        try {
            const response = await apiClient.get('/api/admin/cdn/domains');
            if (response.data.success) {
                let domainsData = response.data.data || [];

                // 如果选择了用户，前端过滤
                if (selectedUser) {
                    domainsData = domainsData.filter(d => d.user_id === selectedUser);
                }

                // 如果有搜索，前端过滤
                if (domainSearch) {
                    domainsData = domainsData.filter(d =>
                        d.domain.toLowerCase().includes(domainSearch.toLowerCase())
                    );
                }

                setDomains(domainsData);
            }
        } catch (error) {
            console.error('Failed to load domains:', error);
        }
    };

    const loadAllStats = async () => {
        setLoading(true);
        const startTime = dateRange[0].unix(); // 发 Unix 秒:服务端按 Asia/Shanghai 解析字符串,非东八区用户会偏移时间窗
        const endTime = dateRange[1].unix();
        const params = { startTime, endTime };
        if (selectedDomain) {
            params.domain_id = selectedDomain;
        } else if (selectedUser) {
            params.user_id = selectedUser;
        }

        // 各面板独立：一个接口失败不清空其它面板
        const calls = [
            ['stats', '/api/admin/traffic/stats'],
            ['traffic', '/api/admin/traffic/map'],
            ['topUrls', '/api/admin/traffic/top-urls'],
            ['status', '/api/admin/traffic/status-codes'],
            ['browsers', '/api/admin/traffic/browsers'],
        ];
        const settled = await Promise.allSettled(calls.map(([, url]) => apiClient.get(url, { params })));
        const next = {};
        const errs = {};
        settled.forEach((r, i) => {
            const key = calls[i][0];
            const body = r.status === 'fulfilled' ? r.value.data : null;
            if (!body?.success) {
                errs[key] = body?.error || r.reason?.response?.data?.error || r.reason?.message || '加载失败';
                return;
            }
            if (key === 'stats') {
                const d = body.data || {};
                next.totalRequests = d.totalRequests || d.total_requests || 0;
                next.totalTraffic = d.totalTraffic || d.total_traffic || 0;
                next.cacheHitRate = d.cacheHitRate || d.cache_hit_rate || 0;
                next.avgResponseTime = d.avgResponseTime || d.avg_response_time || 0;
            } else {
                next[key] = Array.isArray(body.data) ? body.data : [];
            }
        });
        setStats(prev => ({ ...prev, ...next }));
        setPanelErrors(errs);
        const failed = Object.keys(errs);
        if (failed.length) {
            message.error(`${failed.length} 项统计加载失败：${errs[failed[0]]}`);
        }
        setLoading(false);
    };

    const panelBody = (key, content) => (panelErrors[key]
        ? <Alert type="error" showIcon message={`加载失败：${panelErrors[key]}`} />
        : content);

    const formatBytes = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('zh-CN').format(num || 0);
    };

    const trafficColumns = [
        {
            title: '国家/地区',
            dataIndex: 'country_name',
            key: 'country_name',
            render: (text, record) => (text || record.country_code ? <CountryLabel code={record.country_code}>{text || record.country_code}</CountryLabel> : '-')
        },
        {
            title: '流量',
            dataIndex: 'traffic',
            key: 'traffic',
            render: (value) => formatBytes(value),
            sorter: (a, b) => a.traffic - b.traffic
        },
        {
            title: '请求数',
            dataIndex: 'requests',
            key: 'requests',
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.requests - b.requests
        }
    ];

    const urlColumns = [
        {
            title: 'URL',
            dataIndex: 'url',
            key: 'url',
            ellipsis: true
        },
        {
            title: '访问次数',
            dataIndex: 'hits',
            key: 'hits',
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.hits - b.hits
        }
    ];

    const statusColumns = [
        {
            title: 'HTTP状态码',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                let color = 'default';
                if (status >= 200 && status < 300) color = 'success';
                else if (status >= 300 && status < 400) color = 'processing';
                else if (status >= 400 && status < 500) color = 'warning';
                else if (status >= 500) color = 'error';
                return <Text type={color === 'success' ? 'success' : color === 'error' ? 'danger' : undefined}>{status}</Text>;
            }
        },
        {
            title: '次数',
            dataIndex: 'count',
            key: 'count',
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.count - b.count
        }
    ];

    const browserColumns = [
        {
            title: '浏览器',
            dataIndex: 'browser',
            key: 'browser'
        },
        {
            title: '访问次数',
            dataIndex: 'count',
            key: 'count',
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.count - b.count
        }
    ];

    return (
        <div>
            <Card
                title="CDN 分析"
                extra={
                    <Space>
                        <Select
                            showSearch
                            allowClear
                            style={{ width: 200 }}
                            placeholder="筛选用户（默认全部）"
                            value={selectedUser}
                            onChange={(val) => {
                                setSelectedUser(val);
                                setSelectedDomain(null);
                            }}
                            onSearch={setUserSearch}
                            filterOption={false}
                            notFoundContent={loading ? <Spin size="small" /> : <Empty description="无数据" />}
                        >
                            {users.map(user => (
                                <Option key={user.id} value={user.id}>
                                    <UserOutlined /> {user.username} ({user.email})
                                </Option>
                            ))}
                        </Select>
                        <Select
                            showSearch
                            allowClear
                            style={{ width: 200 }}
                            placeholder="筛选域名（默认全部）"
                            value={selectedDomain}
                            onChange={setSelectedDomain}
                            onSearch={setDomainSearch}
                            filterOption={false}
                            notFoundContent={loading ? <Spin size="small" /> : <Empty description="无数据" />}
                        >
                            {domains.map(domain => (
                                <Option key={domain.id} value={domain.id}>
                                    <GlobalOutlined /> {domain.domain}
                                </Option>
                            ))}
                        </Select>
                        <RangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            showTime
                            format="YYYY-MM-DD HH:mm:ss"
                            placeholder={['开始时间', '结束时间']}
                        />
                    </Space>
                }
            >
                <Tabs
                    activeKey={tab}
                    onChange={setTab}
                    destroyInactiveTabPane={false}
                    items={[
                        {
                            key: 'traffic',
                            label: '流量',
                            children: (
                <Spin spinning={loading}>
                    {panelErrors.stats && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={`概览加载失败：${panelErrors.stats}`} />}
                    {/* 统计卡片 */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title="总流量"
                                    value={formatBytes(stats.totalTraffic)}
                                    prefix={<CloudServerOutlined />}
                                    valueStyle={{ color: '#3f8600' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title="总请求数"
                                    value={formatNumber(stats.totalRequests)}
                                    prefix={<LineChartOutlined />}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title="缓存命中率"
                                    value={stats.cacheHitRate}
                                    suffix="%"
                                    prefix={<ThunderboltOutlined />}
                                    valueStyle={{ color: stats.cacheHitRate > 80 ? '#3f8600' : '#cf1322' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title="覆盖国家/地区"
                                    value={stats.traffic.filter(r => r.country_code).length}
                                    prefix={<GlobalOutlined />}
                                    valueStyle={{ color: '#722ed1' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Divider />

                    {/* 详细统计表格 */}
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <Card
                                title="流量分布（按地区）"
                                size="small"
                            >
                                {panelBody('traffic', stats.traffic.length > 0 ? (
                                    <Table
                                        columns={trafficColumns}
                                        dataSource={stats.traffic}
                                        rowKey={(record) => `${record.country_code}-${record.country_name}`}
                                        pagination={{ pageSize: 10 }}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description="暂无数据" />
                                ))}
                            </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card
                                title="热门URL"
                                size="small"
                            >
                                {panelBody('topUrls', stats.topUrls.length > 0 ? (
                                    <Table
                                        columns={urlColumns}
                                        dataSource={stats.topUrls}
                                        rowKey="url"
                                        pagination={{ pageSize: 10 }}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description="暂无数据" />
                                ))}
                            </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card
                                title="HTTP状态码分布"
                                size="small"
                            >
                                {panelBody('status', stats.status.length > 0 ? (
                                    <Table
                                        columns={statusColumns}
                                        dataSource={stats.status}
                                        rowKey="status"
                                        pagination={{ pageSize: 10 }}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description="暂无数据" />
                                ))}
                            </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card
                                title="浏览器分布"
                                size="small"
                            >
                                {panelBody('browsers', stats.browsers.length > 0 ? (
                                    <Table
                                        columns={browserColumns}
                                        dataSource={stats.browsers}
                                        rowKey="browser"
                                        pagination={{ pageSize: 10 }}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description="暂无数据" />
                                ))}
                            </Card>
                        </Col>
                    </Row>
                </Spin>
                            ),
                        },
                        {
                            key: 'waf',
                            label: 'WAF',
                            children: dateRange && dateRange[0] && dateRange[1]
                                ? <WafAnalytics dateRange={dateRange} domainId={selectedDomain} />
                                : <Empty description="请选择时间范围" />,
                        },
                    ]}
                />
            </Card>
        </div>
    );
};

export default Analytics;
